<template>
  <div
    class="min-h-screen min-h-dvh w-full flex flex-col items-center justify-center relative overflow-hidden pb-24 bg-[var(--theme-bg)]"
  >
    <!-- Background -->
    <div class="absolute inset-0 -z-10" aria-hidden="true">
      <div class="absolute inset-0 bg-[var(--theme-bg)]" />
      <div
        class="absolute inset-0 opacity-[0.03]"
        :class="code === 503 ? 'bg-[var(--theme-primary)]' : 'bg-[var(--theme-text)]'"
        style="background-image: radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0); background-size: 40px 40px;"
      />
      <div
        v-if="code === 503"
        class="absolute inset-0 bg-gradient-to-b from-[var(--theme-primary)]/5 via-transparent to-transparent"
      />
    </div>

    <Head :title="`${code} - ${headingText}`" />

    <main class="w-full max-w-[420px] px-6 text-center">
      <!-- Code -->
      <div
        class="inline-flex items-center justify-center rounded-2xl px-4 py-2 mb-8 text-sm font-semibold tracking-wide uppercase"
        :class="badgeClass"
      >
        {{ code }}
      </div>

      <!-- Icon + Code number -->
      <div class="mb-8">
        <div
          class="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-transform duration-300"
          :class="iconWrapClass"
        >
          <!-- 404 -->
          <svg
            v-if="code === 404"
            class="w-10 h-10 sm:w-12 sm:h-12"
            :class="iconColorClass"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- 403 -->
          <svg
            v-else-if="code === 403"
            class="w-10 h-10 sm:w-12 sm:h-12"
            :class="iconColorClass"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <!-- 429 -->
          <svg
            v-else-if="code === 429"
            class="w-10 h-10 sm:w-12 sm:h-12"
            :class="iconColorClass"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- 503 -->
          <svg
            v-else-if="code === 503"
            class="w-10 h-10 sm:w-12 sm:h-12"
            :class="iconColorClass"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <!-- 500 / default -->
          <svg
            v-else
            class="w-10 h-10 sm:w-12 sm:h-12"
            :class="iconColorClass"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1
          class="text-[4rem] sm:text-[5.5rem] font-bold tabular-nums tracking-tight leading-none"
          :class="codeClass"
        >
          {{ code }}
        </h1>
      </div>

      <!-- Heading -->
      <h2 class="text-xl sm:text-2xl font-semibold text-[var(--theme-text)] mb-3">
        {{ headingText }}
      </h2>

      <!-- Message -->
      <p class="text-[var(--theme-text-secondary)] text-base sm:text-lg leading-relaxed mb-10 max-w-[320px] mx-auto">
        {{ messageText }}
      </p>

      <!-- 503: We'll be back soon + only Refresh Page (full navigation to home) -->
      <template v-if="code === 503">
        <p class="text-[var(--theme-primary)] font-medium text-sm mb-6">
          {{ backSoonTextDisplay }}
        </p>
        <a
          :href="homeUrl"
          class="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-xl font-semibold bg-[var(--theme-primary)] text-[var(--theme-text-on-primary)] hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-[var(--theme-primary)]/20"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {{ linkTextDisplay }}
        </a>
      </template>

      <!-- Other errors: only Back to Home -->
      <template v-else>
        <Link
          :href="linkUrl"
          class="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-xl font-semibold bg-[var(--theme-primary)] text-[var(--theme-text-on-primary)] hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-[var(--theme-primary)]/20"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          {{ linkTextDisplay }}
        </Link>
      </template>
    </main>

    <p class="absolute bottom-6 inset-x-0 text-center text-xs text-[var(--theme-text-secondary)]/60">
      {{ siteNameDisplay }}
    </p>
  </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNamespaceLoaded, getLocale } from '@/plugins/i18n';

const { t, locale } = useI18n();

const props = defineProps({
  code: { type: [Number, String], required: true },
  heading: { type: String, required: true },
  message: { type: String, required: true },
  linkText: { type: String, required: true },
  linkUrl: { type: String, required: true },
  backSoonText: { type: String, default: "We'll be back shortly." },
  homeText: { type: String, default: 'Back to Home' },
  homeUrl: { type: String, default: '/' },
  siteName: { type: String, default: '' },
});

const codeKey = computed(() => String(props.code));

// Track whether the error namespace is loaded; start with a sync check
// and re-check on mount (after the navigate handler has run loadFromCacheSync)
const errorNsReady = ref(isNamespaceLoaded(locale.value || getLocale(), 'error'));
onMounted(() => {
  if (!errorNsReady.value) {
    errorNsReady.value = isNamespaceLoaded(locale.value || getLocale(), 'error');
  }
});

function tryTranslate(key, fallback) {
  if (!errorNsReady.value) return fallback;
  const translated = t(key);
  return translated !== key ? translated : fallback;
}

const headingText = computed(() => {
  const specific = tryTranslate(`error.pages.heading_${codeKey.value}`, null);
  if (specific) return specific;
  return tryTranslate('error.pages.heading_default', props.heading);
});

const messageText = computed(() => {
  const specific = tryTranslate(`error.pages.message_${codeKey.value}`, null);
  if (specific) return specific;
  return tryTranslate('error.pages.message_default', props.message);
});

const linkTextDisplay = computed(() => {
  if (props.code === 503) {
    return tryTranslate('error.pages.refresh_page', props.linkText);
  }
  return tryTranslate('error.pages.link_text', props.linkText);
});

const backSoonTextDisplay = computed(() => {
  return tryTranslate('error.pages.back_soon_text', props.backSoonText);
});

const siteNameDisplay = computed(() => {
  if (props.siteName) return props.siteName;
  return tryTranslate('error.pages.site_name', '');
});

const badgeClass = computed(() => {
  if (props.code === 503) return 'bg-[var(--theme-primary)]/15 text-[var(--theme-primary)]';
  if ([403, 500].includes(Number(props.code))) return 'bg-[var(--theme-error)]/10 text-[var(--theme-error)]';
  if (props.code === 429) return 'bg-[var(--theme-warning)]/15 text-[var(--theme-warning)]';
  return 'bg-[var(--theme-text)]/10 text-[var(--theme-text-secondary)]';
});

const iconWrapClass = computed(() => {
  if (props.code === 503) return 'bg-[var(--theme-primary)]/10 ring-1 ring-[var(--theme-primary)]/20';
  if ([403, 500].includes(Number(props.code))) return 'bg-[var(--theme-error)]/10 ring-1 ring-[var(--theme-error)]/20';
  if (props.code === 429) return 'bg-[var(--theme-warning)]/10 ring-1 ring-[var(--theme-warning)]/20';
  return 'bg-[var(--theme-surface)] ring-1 ring-[var(--theme-border)]';
});

const iconColorClass = computed(() => {
  if (props.code === 503) return 'text-[var(--theme-primary)]';
  if ([403, 500].includes(Number(props.code))) return 'text-[var(--theme-error)]';
  if (props.code === 429) return 'text-[var(--theme-warning)]';
  return 'text-[var(--theme-text-secondary)]';
});

const codeClass = computed(() => {
  if (props.code === 503) return 'text-[var(--theme-primary)]';
  if ([403, 500].includes(Number(props.code))) return 'text-[var(--theme-error)]';
  if (props.code === 429) return 'text-[var(--theme-warning)]';
  return 'text-[var(--theme-text)]';
});

</script>
